import React from 'react';
import styles from './FiltersPanel.module.css';
import { CountrySelect } from '../../components/CountrySelect';
import * as dashboardApi from '../../api/dashboardApi';

interface Props {
  token?: string | null;
  selectedCountry: string;
  setSelectedCountry: (c: string) => void;
  jurisdictions: dashboardApi.Jurisdiction[];
  selectedJurisdiction: string;
  setSelectedJurisdiction: (j: string) => void;
  availableJurisdictionTypes: string[];
  jurisdictionFilters: Record<string, boolean>;
  setJurisdictionFilters: (f: Record<string, boolean>) => void;
  availableRegulationTypes: string[];
  policyTypeFilters: Record<string, boolean>;
  setPolicyTypeFilters: (f: Record<string, boolean>) => void;
  dateFilterType: string;
  setDateFilterType: (d: any) => void;
  selectedTimeRange: string;
  handleTimeRangeChange: (v: any) => void;
  showCustomDatePicker: boolean;
  customStartDate: string;
  customEndDate: string;
  setCustomStartDate: (d: string) => void;
  setCustomEndDate: (d: string) => void;
  isLoadingJurisdictions?: boolean;
  showDateFilters?: boolean;
  showRecentNote?: boolean;
  showActiveAlertsNote?: boolean;
}

const FiltersPanel: React.FC<Props> = ({
  token,
  selectedCountry,
  setSelectedCountry,
  jurisdictions,
  selectedJurisdiction,
  setSelectedJurisdiction,
  availableJurisdictionTypes,
  jurisdictionFilters,
  setJurisdictionFilters,
  availableRegulationTypes,
  policyTypeFilters,
  setPolicyTypeFilters,
  dateFilterType,
  setDateFilterType,
  selectedTimeRange,
  handleTimeRangeChange,
  showCustomDatePicker,
  customStartDate,
  customEndDate,
  setCustomStartDate,
  setCustomEndDate,
  isLoadingJurisdictions,
  showDateFilters = true,
  showRecentNote = false,
  showActiveAlertsNote = false,
}) => {
    const excludedJurisdictionTypes = new Set([
    'constituent country',
    'country',
    'federal',
    'state',
    'supranational',
  ]);

  const visibleJurisdictionTypes = (availableJurisdictionTypes || []).filter((t) => {
    const normalized = t.toLowerCase().replace('_', ' ');
    return !excludedJurisdictionTypes.has(normalized);
  });

    return (
    <aside className={styles.filtersPanel}>
      {showRecentNote && (
        <div className={styles.filtersNote}>Recent Activity shows items from the last 7 days — the page may appear empty if there are no updates in that window.</div>
      )}
      {showActiveAlertsNote && (
        <div className={styles.filtersNote}>Active Alerts shows items from the last 24 hours.</div>
      )}
      <div className={styles.filterSection}>
        <CountrySelect token={token || ''} value={selectedCountry} onChange={setSelectedCountry} label="Country" style={{ marginBottom: 12 }} className={styles.filterSelect} />
        <label className={styles.filterLabel} htmlFor="jurisdiction-select">Jurisdiction</label>
        <select id="jurisdiction-select" value={selectedJurisdiction} onChange={(e) => setSelectedJurisdiction(e.target.value)} className={styles.filterSelect} disabled={isLoadingJurisdictions}>
          <option value="All">All Jurisdictions</option>
          {jurisdictions?.map((j) => (<option key={j.id} value={j.id}>{j.name}</option>))}
        </select>
        <div className={styles.checkboxGroup}>
          {visibleJurisdictionTypes.map((type) => (
            <label key={type} className={styles.checkboxItem}>
              <input type="checkbox" checked={jurisdictionFilters[type] ?? false} onChange={(event) => setJurisdictionFilters({ ...jurisdictionFilters, [type]: event.target.checked })} />
              <span>{type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}</span>
            </label>
          ))}
        </div>
      </div>

      {showDateFilters && (
        <div className={styles.filterSection}>
          <label className={styles.filterLabel} htmlFor="time-range-select">Time Range</label>
          <select id="time-range-select" value={selectedTimeRange} onChange={(e) => handleTimeRangeChange(e.target.value)} className={styles.filterSelect}>
            <option value="All">All</option>
            <option value="7D">Last 7 Days</option>
            <option value="30D">Last 30 Days</option>
            <option value="90D">Last 90 Days</option>
            <option value="1Y">Last Year</option>
            <option value="Custom">Custom Range</option>
          </select>

          {showCustomDatePicker && selectedTimeRange === 'Custom' && (
            <div className={styles.customDatePicker}>
              <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className={styles.dateInput} placeholder="Start Date" />
              <span className={styles.dateSeparator}>to</span>
              <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className={styles.dateInput} placeholder="End Date" />
            </div>
          )}
        </div>
      )}

      {/* Policy Type removed per design — only Country and Jurisdiction filters remain */}
    </aside>
  );
};

export default FiltersPanel;
